import type { ContractType } from '@/components/hooks/useContractsData';

// Best-effort UX sync only, never a correctness guarantee: browsers without
// BroadcastChannel (Safari < 15.4) and isolated private-browsing sessions silently
// degrade to the old per-tab behavior, and two tabs can still POST an accept within
// one round-trip — the backend must treat repeat accepts as idempotent.
const CHANNEL_NAME = 'w8shipping-contract-acceptance';

type ContractAcceptedListener = (contractType: ContractType) => void;

const listeners = new Set<ContractAcceptedListener>();
let channel: BroadcastChannel | null = null;
let pageLifecycleAttached = false;

// An open BroadcastChannel makes the page ineligible for the browser back/forward
// cache, so close it on pagehide and recreate it when the page is restored.
function attachPageLifecycle() {
    if (pageLifecycleAttached) {
        return;
    }
    pageLifecycleAttached = true;
    window.addEventListener('pagehide', () => {
        channel?.close();
        channel = null;
    });
    window.addEventListener('pageshow', () => {
        if (listeners.size > 0) {
            getChannel();
        }
    });
}

function canUseBroadcastChannel() {
    return typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined';
}

// Feature detection isn't enough: the constructor itself can throw in restricted
// contexts (e.g. blocked storage, opaque origins) — degrade silently there too.
function createChannel(): BroadcastChannel | null {
    try {
        return new BroadcastChannel(CHANNEL_NAME);
    } catch {
        return null;
    }
}

// BroadcastChannel delivers only to OTHER tabs of the same origin, never back to the
// sender, so subscribers don't need to filter out their own broadcasts.
function getChannel(): BroadcastChannel | null {
    if (!canUseBroadcastChannel()) {
        return null;
    }

    attachPageLifecycle();

    if (!channel) {
        channel = createChannel();
        if (channel) {
            channel.onmessage = event => {
                const contractType = event.data?.contractType;
                if (typeof contractType !== 'string') {
                    return;
                }
                listeners.forEach(listener => listener(contractType as ContractType));
            };
        }
    }

    return channel;
}

export function broadcastContractAccepted(contractType: ContractType) {
    if (!canUseBroadcastChannel()) {
        return;
    }

    // With no subscribers in this tab there is no reason to keep a persistent channel
    // open (an open channel makes the page ineligible for the back/forward cache);
    // a closed channel still delivers the already-posted message.
    if (listeners.size === 0) {
        const tempChannel = createChannel();
        tempChannel?.postMessage({ contractType });
        tempChannel?.close();
        return;
    }

    getChannel()?.postMessage({ contractType });
}

export function subscribeContractAccepted(listener: ContractAcceptedListener) {
    getChannel();
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            channel?.close();
            channel = null;
        }
    };
}
